import React, { useMemo } from 'react'
import { Link } from 'react-router-dom'

import { useTheme, Typography, IconButton } from '@mui/material'
import { ArrowUpRightIcon, PrinterIcon } from 'lucide-react'

import { Button } from '@repo/ui'
import {
  StatusChipCell,
  StatusChip,
  ActionDropdown,
  CompanyCell,
  ProductGroupCell,
  UserCell,
  AddressCell,
} from 'components'
import { statusFilterOptions } from 'components/input/filters/StatusChipFilter'
import { ORDER_STATUSES, PAYMENT_STATUSES } from 'constants/statuses'
import { printLabel } from 'pages/internal/OrderDetailPage/components'
import { ROUTES } from 'routes'
import { relativeOrDateTime, toDollar, openExternalLink } from 'utils'
import type { ShopifyOrder } from 'store/orders/ordersStore'

import { getSingleShipment } from './useOrderRowActions'

/**
 * VRG-512 (§71) — ONE definition of the orders table's columns, rendered by both
 * `/internal/orders` (the standalone page) and the company detail page's Orders
 * tab. The tab previously declared 4 columns of its own against the page's 15, and
 * they had already drifted in kind, not just in count: the panel's order id was
 * dead text where the page's is a link, its status column read a different field
 * under a different header, and it fell back to plain body text for any status
 * `ORDER_STATUSES` did not carry, where the page always renders a chip.
 *
 * A HOOK, not a constant: the columns close over `theme` (the Assignment chip's
 * colour buckets) and the referrer (§42) — and the actions column closes over
 * handlers. `ROLE_MEMBER_COLUMNS` in `RolePageSkeleton` is a plain array because it
 * feeds a skeleton, which has no behaviour to close over; it is not the model here.
 *
 * What is SHARED: the column definitions, their cells, the filter/sort config, and
 * the row actions (via `getActionItems`, from `useOrderRowActions`).
 * What each surface KEEPS: its own `Header`/breadcrumb, page gutters, URL state,
 * pagination mode, and its height strategy (§22 — `fillBottom` vs `topOffset`).
 *
 * §49 — widths are sized for the HEADER, not the values, and are unchanged from the
 * page they were extracted from; verify at 1280px when adding one.
 */

interface UseOrderColumnsParams {
  /** Router state from `useReferrerState()`, for the Order # link and the kebab's Details item (§42). */
  referrer?: { backTo: string; backLabel: string }
  /** Row kebab items — from `useOrderRowActions`, so gating is shared too. */
  getActionItems: (row: ShopifyOrder) => any[]
  /**
   * Fields to leave out on this surface. The definitions stay shared; a surface
   * only ever SUBTRACTS from them, never restates one. Used by the company detail
   * tab to drop `company`, which is a constant inside one company's page.
   */
  omitFields?: string[]
}

export const useOrderColumns = ({ referrer, getActionItems, omitFields }: UseOrderColumnsParams) => {
  const theme = useTheme()

  return useMemo(() => {
    const columns = [
      {
        field: 'name',
        headerName: 'Order #',
        width: 160,
        pinned: 'left',
        type: 'string',
        renderCell: ({ row }: { row: ShopifyOrder }) => (
          // Real anchor (react-router Link) — a plain Typography onClick gets
          // swallowed by the DataGrid cell; an <a> navigates natively.
          <Link
            to={ROUTES.INTERNAL_ORDER(encodeURIComponent(String(row.id)))}
            state={referrer}
            style={{
              textDecoration: 'none',
              display: 'flex',
              alignItems: 'center',
              width: '100%',
              height: '100%',
              minWidth: 0,
            }}
          >
            {/*
              VRG-512 — NO seeded-sample badge here, deliberately. Both surfaces
              rendering these columns are `/internal/*`, the Vera-staff console,
              and no sample/intro data is to surface there at all: a staff view
              whose job is showing what is really happening across brands has
              nothing to annotate, because it should never hold a seeded row.
              The badge belongs on the BRAND-facing surfaces (Products,
              Certificates, People, Address Book), where it still renders.
            */}
            <Typography
              variant="body2"
              fontWeight="medium"
              noWrap
              sx={{ cursor: 'pointer', color: 'text.primary', '&:hover': { textDecoration: 'underline' } }}
            >
              {row.name}
            </Typography>
          </Link>
        ),
      },
      {
        field: 'company',
        headerName: 'Company',
        type: 'string',
        width: 220,
        renderCell: ({ row }: { row: ShopifyOrder }) => (
          row.company
            ? <CompanyCell company={{ id: row.company.id, name: row.company.name, logo: '' }} />
            : null
        ),
      },
      {
        field: 'lineItems',
        headerName: 'Products',
        type: 'string',
        width: 250,
        renderCell: ({ row }: { row: ShopifyOrder }) => {
          // Exclude the no-variant "Customization setup fee" line — it's not a
          // product tile (and its missing variant would crash the map below).
          const products = row.lineItems
            .filter(item => item.variant?.id)
            .map(item => ({
              id: item.id,
              title: item.title,
              sku: item.variant.sku,
              quantity: item.quantity,
              price: parseFloat(item.variant.price),
              // ProductGroupCell reads `images?.[0]`; the list line item carries a
              // single Shopify image. Omit when absent so the cell renders blank
              // (no placeholder tile) rather than a broken-image square (§37).
              images: item.image ? [item.image] : undefined,
            }));
          return <ProductGroupCell products={products} route={ROUTES.TAGS_SHOP_PRODUCT} showQuantityBadge/>
        },
      },
      {
        field: 'totalPriceSet',
        headerName: 'Total',
        type: 'number',
        width: 100,
        renderCell: ({ row }: { row: ShopifyOrder }) => {
          const amount = parseFloat(row.totalPriceSet.shopMoney.amount);
          return toDollar(amount);
        },
      },
      {
        field: 'createdAt',
        headerName: 'Created',
        type: 'dateTime',
        width: 170,
        renderCell: ({ row }: { row: ShopifyOrder }) => relativeOrDateTime(row.createdAt),
      },
      {
        field: 'customer',
        headerName: 'Placed by',
        type: 'string',
        width: 260,
        renderCell: ({ row }: { row: ShopifyOrder }) => (
          <UserCell
            user={{
              first_name: row.customer.firstName,
              last_name: row.customer.lastName,
              email: row.customer.email,
            }}
            route={ROUTES.INTERNAL_USERS}
          />
        ),
      },
      {
        // VERA-891: package-assignment progress — how many of the order's line
        // items have a package assigned. 0/N = neutral chrome (a starting state,
        // not an error), partial = yellow, N/N = green (ready to ship). Sits left
        // of Fulfillment Status.
        field: 'assignedLineItemCount',
        headerName: 'Assignment',
        width: 130,
        sortable: false,
        filterable: false,
        renderCell: ({ row }: { row: ShopifyOrder }) => {
          const total = row.lineItems?.length ?? 0
          if (total === 0) return null
          const assigned = Math.min(row.assignedLineItemCount ?? 0, total)
          const bucket = assigned === 0 ? 'empty' : assigned >= total ? 'complete' : 'partial'
          const color =
            assigned === 0
              ? theme.palette.text.secondary
              : assigned >= total
                ? theme.palette.success.main
                : theme.palette.warning.main
          // Reuse the shared StatusChip (same component Fulfillment/Payment Status
          // render through) for exact size/padding parity — a fresh single-key
          // statuses map per row since the N/M label is dynamic.
          return (
            <StatusChip
              size="small"
              statuses={{ [bucket]: { label: `${assigned}/${total}`, color } }}
              status={bucket}
            />
          )
        },
      },
      {
        field: 'fulfillmentStatus',
        headerName: 'Fulfillment',
        type: 'statusChip',
        options: statusFilterOptions(ORDER_STATUSES),
        width: 150,
        // `statuses` drives the shared MultiSelectFilter's status-pill treatment
        // (highlighted/grey toggle chips) in the filter drawer.
        statuses: ORDER_STATUSES,
        renderCell: ({ value }: { value: string }) => (
          <StatusChipCell status={value} statuses={ORDER_STATUSES} />
        ),
      },
      {
        field: 'financialStatus',
        headerName: 'Payment',
        type: 'statusChip',
        options: statusFilterOptions(PAYMENT_STATUSES),
        width: 150,
        statuses: PAYMENT_STATUSES,
        renderCell: ({ value }: { value: string }) => (
          <StatusChipCell status={value} statuses={PAYMENT_STATUSES} />
        ),
      },
      {
        // VERA-891: the customer-facing shipping charge — Shopify's own
        // totalShippingPriceSet on the order (set at checkout/draft-order
        // creation), NOT what Vera pays for the physical label purchase
        // (Shopify's label-purchase API doesn't expose that — see
        // ShopifyServiceProvider.purchaseShippingLabel's doc comment).
        field: 'shippingPrice',
        headerName: 'Shipping Price',
        type: 'number',
        width: 130,
        sortable: false,
        filterable: false,
        renderCell: ({ row }: { row: ShopifyOrder }) => {
          const amount = row.shippingPriceSet?.shopMoney?.amount
          return amount != null
            ? <Typography variant="body2" color="text.secondary">{toDollar(parseFloat(amount))}</Typography>
            : null
        },
      },
      {
        field: 'shippingLabel',
        headerName: 'Shipping Label',
        width: 140,
        sortable: false,
        filterable: false,
        renderCell: ({ row }: { row: ShopifyOrder }) => {
          const shipment = getSingleShipment(row)
          if (!shipment?.labelUrl || shipment.labelStatus !== 'purchased') return null
          return (
            <Button
              disableElevation
              variant="tertiary"
              startIcon={<PrinterIcon strokeWidth={1} size={14} />}
              onClick={() => printLabel(shipment.labelUrl!)}
            >
              Print
            </Button>
          )
        },
      },
      {
        field: 'trackingNumber',
        headerName: 'Tracking Number',
        type: 'string',
        width: 150,
        renderCell: ({ value }: { value: string }) => (
          value
            ? <Typography variant="body2" color="text.secondary">{value}</Typography>
            : null
        ),
      },
      {
        field: 'shippingAddress',
        headerName: 'Shipping Address',
        type: 'string',
        width: 250,
        renderCell: ({ row }: { row: ShopifyOrder }) => (
          row.shippingAddress?.address1
            ? <AddressCell
                address={{
                  address: row.shippingAddress.address1,
                  city: row.shippingAddress.city,
                  country: row.shippingAddress.country,
                  zip: row.shippingAddress.zip,
                }}
              />
            : null
        ),
      },
      {
        field: 'adminUrl',
        headerName: 'Shopify',
        width: 90,
        sortable: false,
        filterable: false,
        align: 'center',
        headerAlign: 'center',
        renderCell: ({ row }: { row: ShopifyOrder }) => (
          row.adminUrl
            ? <IconButton size="small" onClick={() => openExternalLink(row.adminUrl!)}>
                <ArrowUpRightIcon strokeWidth={1} size={16} />
              </IconButton>
            : null
        ),
      },
      {
        field: 'actions',
        headerName: false,
        pinned: 'right',
        sortable: false,
        disableReorder: true,
        align: 'center',
        width: 54,
        renderCell: ({ row }: { row: ShopifyOrder }) => (
          <ActionDropdown items={getActionItems(row)} />
        ),
      },
    ]

    return omitFields?.length
      ? columns.filter(column => !omitFields.includes(column.field))
      : columns
    // `theme` is stable per theme mode; `getActionItems` is memoised by its own
    // hook on the same [navigate, referrer] identity, so this rebuilds exactly
    // when the page's columns used to. `omitFields` must be a stable reference
    // (a module-level constant) — an inline literal rebuilds this every render.
  }, [referrer, getActionItems, theme, omitFields])
}
